import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag } from "antd";
import { Link } from "react-router-dom";
import { EditInfo, DeleteModal } from ".";
import { getOffersList, patchOfferStatus } from "../../appRedux/actions/offers";

function ListView() {
  const dispatch = useDispatch();
  const offerList = useSelector((state) => state.offers.offerList);
  const queryInfo = useSelector((state) => state.offers.queryInfo);
  const totalRecords = useSelector((state) => state.offers.totalRecords);
  const pages = useSelector((state) => state.offers.pages);

  useEffect(() => {
    dispatch(getOffersList(queryInfo)).catch(() => {});
  }, []);

  const handleTableChange = (pagination) => {
    const page = pagination?.current || 1;
    const size = pagination?.pageSize || queryInfo.size;
    dispatch(getOffersList({ ...queryInfo, page, size })).catch(() => {});
  };

  const discountLabel = (record) => {
    if (record.discountType === "PERCENT") return `${record.discountValue}% off`;
    return `₹${record.discountValue} off`;
  };

  const columns = [
    {
      title: "Title & Details",
      dataIndex: "title",
      key: "title",
      align: "left",
      render: (title, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>{title || "—"}</span>
          {record.description && (
            <span style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
              {record.description.length > 50 ? `${record.description.substring(0, 50)}...` : record.description}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      align: "center",
      render: (level) => {
        const color =
          level === "GLOBAL"
            ? "geekblue"
            : level === "MERCHANT"
            ? "cyan"
            : "purple";
        return <Tag color={color} style={{ borderRadius: '4px', padding: '2px 8px' }}>{level}</Tag>;
      },
    },
    {
      title: "Target Scope",
      key: "scope",
      align: "center",
      render: (_, record) => {
        if (record.level === "MERCHANT")
          return record.merchant ? (
            <Tag color="cyan" style={{ borderRadius: '4px', color: '#006d75', borderColor: '#87e8de', background: '#e6fffb' }}><i className="fa fa-building-o mr-1" /> {record.merchant}</Tag>
          ) : (
            <span style={{ color: "#aaa" }}>—</span>
          );
        if (record.level === "COURSE")
          return record.courseId?.title ? (
            <div style={{ display: "inline-block", maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={record.courseId.title}>
              <i className="fa fa-graduation-cap mr-1 text-muted" /> {record.courseId.title}
            </div>
          ) : (
            <span style={{ color: "#aaa" }}>{record.courseId || "—"}</span>
          );
        return <span style={{ color: "#aaa" }}>—</span>;
      },
    },
    {
      title: "Discount",
      key: "discount",
      align: "center",
      render: (_, record) => {
        const isPercent = record.discountType === "PERCENT";
        return (
          <Tag color="green" style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #b7eb8f', backgroundColor: '#f6ffed' }}>
            <strong>{isPercent ? `${record.discountValue}%` : `₹${record.discountValue}`}</strong> off
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const color =
          status === "ACTIVE" ? "success" : status === "DRAFT" ? "default" : "error";
        return <Tag color={color} style={{ borderRadius: '4px' }}>{status}</Tag>;
      },
    },
    {
      title: "Validity",
      key: "validity",
      align: "center",
      render: (_, record) => {
        if (!record.startAt && !record.endAt) return <Tag color="default" style={{ borderRadius: '4px' }}>Lifetime</Tag>;
        const start = record.startAt
          ? new Date(record.startAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : "—";
        const end = record.endAt
          ? new Date(record.endAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : "—";
        return (
          <div style={{ display: "inline-block", textAlign: "left", fontSize: "12px", color: "#555" }}>
            <div><i className="fa fa-calendar-check-o text-success mr-1" /> {start}</div>
            <div><i className="fa fa-calendar-times-o text-danger mr-1" /> {end}</div>
          </div>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-center" style={{ padding: "8px" }}>
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <EditInfo info={record} key="edit_offer" />
            {record.status === "ACTIVE" ? (
              <button
                className="dropdown-item"
                onClick={() =>
                  dispatch(
                    patchOfferStatus(record._id, "INACTIVE")
                  )
                    .then(() => {
                      dispatch(getOffersList(queryInfo)).catch(() => {});
                    })
                    .catch(() => {})
                }
              >
                <i className="fa fa-pause m-r-5" /> Deactivate
              </button>
            ) : (
              <button
                className="dropdown-item"
                onClick={() =>
                  dispatch(
                    patchOfferStatus(record._id, "ACTIVE")
                  )
                    .then(() => {
                      dispatch(getOffersList(queryInfo)).catch(() => {});
                    })
                    .catch(() => {})
                }
              >
                <i className="fa fa-play m-r-5" /> Activate
              </button>
            )}
            <DeleteModal info={record} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="table-responsive">
      <Table
        className="platform-hub-table"
        rowKey="_id"
        columns={columns}
        dataSource={offerList}
        pagination={{
          current: queryInfo?.page || 1,
          pageSize: queryInfo?.size || 10,
          total: totalRecords,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "50"],
          showTotal: (total) => `Total ${total} offers`,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
}

export default ListView;
